import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Game } from "@/types/game";
import { MaterialIcon } from "@/components/ui/material-icon";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/contexts/CartContext";
import { flyToBag } from "@/lib/flyToBag";
import AnimatedSection from "./AnimatedSection";
import LazyImage from "./LazyImage";
import GameCardSkeleton from "./skeletons/GameCardSkeleton";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import { getPrimaryGameImage, getSecondaryGameImage } from "@/lib/gameImages";

export const getGameSlug = (title: string) => {
  return title
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
};

interface FeaturedGamesGridProps {
  queryKey?: string;
  games?: Game[];
  sectionLabel?: string;
  sectionTitle?: string;
  viewAllHref?: string;
  viewAllLabel?: string;
  excludeGameIds?: string[];
  filter?: (game: Game) => boolean;
  maxItems?: number;
  showAddToBag?: boolean;
  forceColumns?: number;
  staggered?: boolean;
  paddingTopClass?: string;
}

const FeaturedGamesGrid = ({
  queryKey,
  games: propGames,
  sectionLabel,
  sectionTitle,
  viewAllHref,
  viewAllLabel = "View All",
  excludeGameIds,
  filter,
  maxItems,
  showAddToBag = true,
  forceColumns,
  staggered = true,
  paddingTopClass = "pt-48",
}: FeaturedGamesGridProps) => {
  const { settings } = useSiteSettings();
  const { addItem } = useCart();

  const columns =
    forceColumns ?? (settings.featured_games_columns === "3" ? 3 : settings.featured_games_columns === "2" ? 2 : 4);
  const gridClass =
    columns === 2
      ? "md:grid-cols-1 lg:grid-cols-2"
      : columns === 3
        ? "md:grid-cols-1 lg:grid-cols-3"
        : "md:grid-cols-1 lg:grid-cols-4";

  const { data: fetchedGames, isLoading } = useQuery({
    queryKey: [queryKey, columns],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("games")
        .select("*, gallery_images:game_gallery_images(*)")
        .eq("is_published", true)
        .eq("featured", true)
        .order("sort_order");

      if (error) throw error;
      let result = data as unknown as Game[];
      if (excludeGameIds?.length) {
        const excludeSet = new Set(excludeGameIds);
        result = result.filter((g) => !excludeSet.has(g.id));
      }
      if (filter) {
        result = result.filter(filter);
      }
      return result;
    },
    enabled: propGames === undefined,
  });

  const games = propGames !== undefined ? propGames : fetchedGames;
  const loading = propGames === undefined ? isLoading : false;
  const displayGames = maxItems && maxItems > 0 && games ? games.slice(0, maxItems) : games;

  if (loading) {
    return (
      <section className="py-48 section-dark relative overflow-hidden">
        <div className="container mx-auto relative z-10">
          {(sectionTitle || sectionLabel) && (
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
              <div className="section-header mb-0">
                {sectionLabel && <span className="section-label">{sectionLabel}</span>}
                {sectionTitle && <h2 className="section-title text-3xl md:text-4xl lg:text-5xl">{sectionTitle}</h2>}
              </div>
            </div>
          )}

          <div className={`grid gap-8 ${gridClass}`}>
            {[...Array(columns)].map((_, i) => (
              <GameCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!displayGames || displayGames.length === 0) {
    return null;
  }

  return (
    <section className={`${paddingTopClass} pb-32 section-dark relative overflow-hidden`}>
      <div className="container mx-auto relative z-10">
        {(sectionTitle || sectionLabel) && (
          <AnimatedSection
            animation="fade-up"
            className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12"
          >
            <div className="section-header mb-0">
              {sectionLabel && <span className="section-label">{sectionLabel}</span>}
              {sectionTitle && (
                <h2 className="section-title text-3xl md:text-4xl lg:text-5xl text-dark-foreground">{sectionTitle}</h2>
              )}
            </div>
            {viewAllHref && (
              <Link to={viewAllHref} className="btn-outline-light group">
                {viewAllLabel}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            )}
          </AnimatedSection>
        )}

        <div className={`grid gap-8 md:gap-10 lg:gap-16 ${staggered && columns === 2 ? "lg:pb-16" : ""} ${gridClass}`}>
          {displayGames.map((game, index) => {
            const primaryImage = getPrimaryGameImage(game);
            const secondaryImage = getSecondaryGameImage(game);

            return (
              <AnimatedSection
                key={game.id}
                animation="fade-up"
                delay={index * 100}
                className={staggered && columns === 2 && index % 2 === 1 ? "" : undefined}
              >
                <Link
                  to={`/games/${getGameSlug(game.title)}`}
                  className="group relative border border-border bg-[#0c0c0c] transition-all duration-500 hover:border-l-blood-red overflow-hidden cursor-pointer flex flex-col md:flex-row lg:flex-col h-full"
                >

                  <div className="relative h-[350px] flex-shrink-0 overflow-hidden md:h-auto md:min-h-[350px] md:w-[55%] lg:h-[350px] lg:min-h-0 lg:w-full">
                    <div className="absolute left-4 top-4 z-10 flex flex-col gap-2">
                      {game.in_development && <span className="in-development-badge">In Development</span>}
                      {game.playtesting && <span className="playtesting-badge">Playtesting</span>}
                      {game.coming_soon && <span className="coming-soon-badge">Coming Soon</span>}
                    </div>
                    {primaryImage ? (
                      <>
                        <div
                          className={`absolute inset-0 transition-transform duration-500 ease-out ${secondaryImage ? "group-hover:-translate-x-full group-focus-visible:-translate-x-full" : ""}`}
                        >
                          <LazyImage src={primaryImage} alt={game.title} className="w-full h-full object-cover" />
                        </div>
                        {secondaryImage && (
                          <div className="absolute inset-0 translate-x-full transition-transform duration-500 ease-out group-hover:translate-x-0 group-focus-visible:translate-x-0">
                            <LazyImage
                              src={secondaryImage}
                              alt={`${game.title} alternate product view`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="w-full h-full bg-muted flex items-center justify-center">
                        <span className="text-muted-foreground text-sm uppercase tracking-wider">No Image</span>
                      </div>
                    )}

                    {showAddToBag && game.gamecrafter_sku && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          addItem(
                            {
                              gameId: game.id,
                              title: game.title,
                              slug: getGameSlug(game.title),
                              sku: game.gamecrafter_sku,
                              price: game.cost ?? null,
                              imageUrl: primaryImage,
                            },
                            1,
                          );
                          flyToBag(e.currentTarget, primaryImage);
                          toast.success(`${game.title} added to bag`, {
                            description: "View your bag in the top right.",
                          });
                        }}
                        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 inline-flex items-center justify-center gap-2 bg-blood-red text-white px-6 py-3 uppercase tracking-[0.2em] text-xs font-semibold opacity-100 translate-y-0 pointer-events-auto transition-all duration-300 lg:opacity-0 lg:translate-y-2 lg:pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:translate-y-0 group-focus-within:pointer-events-auto focus:opacity-100 focus:translate-y-0 focus:pointer-events-auto hover:bg-[#111]"
                      >
                        Add to Bag
                      </button>
                    )}
                  </div>

                  <div className="flex flex-col flex-1 min-h-0 md:w-[45%] lg:w-full">
                    <div className="px-8 pt-8 pb-6">
                      <h3 className="text-2xl font-display font-bold uppercase tracking-widest mb-2 text-card-foreground group-hover:text-blood-red transition-colors">
                        {game.title}
                      </h3>

                      {game.cost != null && game.cost > 0 && game.gamecrafter_sku && (
                        <p className="text-lg font-semibold text-card-foreground tabular-nums">
                          ${game.cost.toFixed(2)}
                        </p>
                      )}

                      {game.short_description && (
                        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{game.short_description}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-3 divide-x divide-border border-t border-border mt-auto">
                      {game.number_of_players && (
                        <div className="flex flex-col items-center gap-0.75 text-center px-1 pt-2 pb-3">
                          <MaterialIcon name="group" size="md" className="text-blood-red" />
                          <span className="text-[11px] leading-tight uppercase tracking-widest text-muted-foreground">
                            {game.number_of_players} Players
                          </span>
                        </div>
                      )}
                      {game.timeframe && (
                        <div className="flex flex-col items-center gap-0.75 text-center px-1 pt-2 pb-3">
                          <MaterialIcon name="schedule" size="md" className="text-blood-red" />
                          <span className="text-[11px] leading-tight uppercase tracking-widest text-muted-foreground">
                            {game.timeframe}
                          </span>
                        </div>
                      )}
                      {game.complexity && (
                        <div className="flex flex-col items-center gap-0.75 text-center px-1 pt-2 pb-3">
                          <MaterialIcon
                            size="md"
                            name={
                              game.complexity.toLowerCase() === "light"
                                ? "stat_0"
                                : game.complexity.toLowerCase() === "medium"
                                  ? "layers"
                                  : "stacks"
                            }
                            className="text-blood-red"
                          />
                          <span className="text-[11px] leading-tight uppercase tracking-widest text-muted-foreground">
                            {game.complexity}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              </AnimatedSection>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturedGamesGrid;
